import React, { useState, useEffect, useRef } from "react";
import dayjs from "dayjs";
import { useHistory } from "react-router-dom";
import {
  Container,
  Modal,
  Box,
  IconButton,
  TextField,
  Button,
} from "@mui/material";
import { useSnackbar } from "notistack";
import DehazeIcon from "@mui/icons-material/Dehaze";
import CloseIcon from "@mui/icons-material/Close";
import Header from "../../components/Header";
import useUser from "../../hooks/useUser";
import { api } from "../../services/Api";
import { formatarDataParaBR } from "../../utils/formatarUTCString";
import SelecionarVisualizacao from "./SelecionarVisualizacao";
import ConteudoMapa from "./ConteudoMapa";
import ModalUnidade from "../MapaCurvas/ModalUnidade";
import "./styles.css";

function mapSituacaoParaStatus(valorSituacao) {
  return valorSituacao === "concluido" ? "removido" : "ativo";
}

function normalizarResposta(data) {
  if (data && typeof data === "object" && "data" in data) {
    return data.data;
  }
  return data;
}

export default function MapaTrace() {
  const { enqueueSnackbar } = useSnackbar();
  const usuario = useUser();
  const history = useHistory();

  const [situacao, setSituacao] = useState("pendente");
  const [dataFim, setDataFim] = useState(new Date());
  const [unidade, setUnidade] = useState(0);
  const [placeholderUnidadeButton, setPlaceholderUnidadeButton] = useState("Unidade");
  const [isModalUnidadeOpen, setIsModalUnidadeOpen] = useState(false);
  const [modalAdicionarAmostraOpen, setModalAdicionarAmostraOpen] = useState(false);
  const [biomeriuexInput, setBiomeriuexInput] = useState("");
  const [consultaTubo, setConsultaTubo] = useState(null);
  const [cards, setCards] = useState([]);
  const [carregandoCards, setCarregandoCards] = useState(false);
  const [processandoConsulta, setProcessandoConsulta] = useState(false);
  const [processandoAdicionar, setProcessandoAdicionar] = useState(false);
  const inputBiomeriuexRef = useRef(null);

  function getUnidadeSelecionada() {
    const valor = localStorage.getItem("@DNACenter:UnidadeDNA");
    const numero = Number(valor);
    return Number.isFinite(numero) && numero > 0 ? numero : 0;
  }

  async function carregarCards(idUnidade, valorSituacao) {
    const unidadeNumero = Number(idUnidade);
    if (!Number.isFinite(unidadeNumero) || unidadeNumero <= 0) {
      setCards([]);
      return;
    }

    setCarregandoCards(true);
    try {
      const status = mapSituacaoParaStatus(valorSituacao);
      const resposta = await api.get(
        `/v2/mapaTrace/cards?id_unidade=${unidadeNumero}&status=${status}&data_fim=${dayjs(dataFim).valueOf()}`
      );
      const lista = normalizarResposta(resposta.data) || [];
      setCards(Array.isArray(lista) ? lista : []);
    } catch (e) {
      const mensagem = (e?.response?.data?.messages ?? "").trim();
      if (mensagem) {
        enqueueSnackbar(mensagem, { variant: "error", autoHideDuration: 3000 });
      }
      setCards([]);
    } finally {
      setCarregandoCards(false);
    }
  }

  useEffect(() => {
    if (!localStorage.getItem("@DNACenter:UnidadeDNA")) {
      setUnidade(0);
      setIsModalUnidadeOpen(true);
    } else {
      setUnidade(getUnidadeSelecionada());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const nomeUnidade = localStorage.getItem("@DNACenter:NomeUnidadeDNA");
    const novoPlaceholder = nomeUnidade !== null ? nomeUnidade : "Selecione a Unidade";
    setPlaceholderUnidadeButton((anterior) =>
      anterior === novoPlaceholder ? anterior : novoPlaceholder
    );
  }, [isModalUnidadeOpen, unidade]);

  useEffect(() => {
    if (unidade) {
      carregarCards(unidade, situacao);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unidade, situacao, dataFim]);

  function handleOpenModalUnidade() {
    setIsModalUnidadeOpen(true);
  }

  function handleCloseModalUnidade() {
    setIsModalUnidadeOpen(false);
  }

  function handleCloseModalAdicionarAmostra() {
    setModalAdicionarAmostraOpen(false);
    setBiomeriuexInput("");
    setConsultaTubo(null);
  }

  async function consultarTubo() {
    if (processandoConsulta || !biomeriuexInput?.trim()) {
      return;
    }

    setProcessandoConsulta(true);
    try {
      const biomeriuex = biomeriuexInput.trim().toUpperCase();
      const resposta = await api.get(
        `/v2/mapaTrace/tubos/consulta/${encodeURIComponent(biomeriuex)}`
      );
      const dados = normalizarResposta(resposta.data);

      setConsultaTubo(dados);
    } catch (e) {
      setConsultaTubo(null);
      const mensagem = (e?.response?.data?.messages ?? "").trim();
      if (mensagem) {
        const variant = e?.response?.status === 404 ? "warning" : "error";
        enqueueSnackbar(mensagem, { variant, autoHideDuration: 3000 });
      }
    } finally {
      setProcessandoConsulta(false);
    }
  }

  async function handleAdicionarAmostra() {
    if (processandoAdicionar) return;

    setProcessandoAdicionar(true);
    try {
      await api.post("/v2/mapaTrace/cards", {
        id_unidade: unidade,
        biomeriuex: consultaTubo.biomeriuex,
      });

      enqueueSnackbar("Amostra adicionada com sucesso.", {
        variant: "success",
        autoHideDuration: 3000,
      });
      handleCloseModalAdicionarAmostra();
      await carregarCards(unidade, situacao);
    } catch (e) {
      const mensagem = (e?.response?.data?.messages ?? "").trim();
      if (mensagem) {
        enqueueSnackbar(mensagem, { variant: "error", autoHideDuration: 3000 });
      }
    } finally {
      setProcessandoAdicionar(false);
    }
  }

  async function confirmarRemocaoCard(id, biomeriuex) {
    try {
      await api.post(`/v2/mapaTrace/cards/${id}/confirmar-remocao`, {
        biomeriuex,
      });
      enqueueSnackbar("Remoção confirmada com sucesso.", {
        variant: "success",
        autoHideDuration: 3000,
      });
      await carregarCards(unidade, situacao);
    } catch (e) {
      const mensagem = (e?.response?.data?.messages ?? "").trim();
      if (mensagem) {
        enqueueSnackbar(mensagem, { variant: "error", autoHideDuration: 3000 });
      }
      throw e;
    }
  }

  useEffect(() => {
    if (modalAdicionarAmostraOpen && inputBiomeriuexRef.current) {
      const timer = setTimeout(() => inputBiomeriuexRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
  }, [modalAdicionarAmostraOpen]);

  function handleBiomeriuexKeyDown(e) {
    if (e.key === "Enter" && biomeriuexInput.trim()) {
      e.preventDefault();
      consultarTubo();
    }
  }

  function handleLimparConsulta() {
    setBiomeriuexInput("");
    setConsultaTubo(null);
    setTimeout(() => inputBiomeriuexRef.current?.focus(), 100);
  }

  return (
    <>
      <Header titulo="Mapa Trace" usuario={usuario} history={history} />

      <Container maxWidth="md">
        <div className="mapa-trace" id="container-aba-ctrl">
          <button
            type="button"
            id="button-aba-ctrl-samples-selected"
            className="button-aba-ctrl"
          >
            Controle de Amostras
          </button>
        </div>
      </Container>

      <div className="mapa-trace">
        <div className="selecionar_visao">
          <SelecionarVisualizacao
            setSituacao={setSituacao}
            situacao={situacao}
            dataFim={dataFim}
            setDataFim={setDataFim}
            onAdicionarAmostra={() => setModalAdicionarAmostraOpen(true)}
          />
        </div>

        <ModalUnidade
          isModalUnidadeOpen={isModalUnidadeOpen}
          handleCloseModalUnidade={handleCloseModalUnidade}
          unidade={unidade}
          setUnidade={setUnidade}
          setPlaceholderUnidadeButton={setPlaceholderUnidadeButton}
        />

        <div id="unidade">
          <h3 id="unidadeNome">{placeholderUnidadeButton}</h3>
          <DehazeIcon
            id="unidadeOpen"
            aria-label="Selecionar unidade"
            onClick={handleOpenModalUnidade}
          />
        </div>

        <ConteudoMapa
          cards={cards}
          loading={carregandoCards}
          onConfirmarRemocao={confirmarRemocaoCard}
        />

        <Modal
          open={modalAdicionarAmostraOpen}
          onClose={handleCloseModalAdicionarAmostra}
          aria-labelledby="modal-adicionar-amostra-title"
          aria-describedby="modal-adicionar-amostra-description"
        >
          <Box
            className={
              consultaTubo
                ? "modal-adicionar-amostra modal-adicionar-amostra--com-consulta"
                : "modal-adicionar-amostra"
            }
          >
            <h2 className="modal-adicionar-amostra-title" id="modal-adicionar-amostra-title">
              Adicionar Amostra
              <IconButton
                aria-label="Fechar"
                onClick={handleCloseModalAdicionarAmostra}
                sx={{ float: "right", marginTop: "-8px" }}
              >
                <CloseIcon />
              </IconButton>
            </h2>
            <p id="modal-adicionar-amostra-description" className="modal-adicionar-amostra-desc">
              Escaneie o código da amostra no leitor.
            </p>
            <TextField
              inputRef={inputBiomeriuexRef}
              label="Código da amostra"
              value={biomeriuexInput}
              onChange={(e) => {
                setBiomeriuexInput(e.target.value);
                if (consultaTubo) setConsultaTubo(null);
              }}
              onKeyDown={handleBiomeriuexKeyDown}
              variant="outlined"
              size="small"
              fullWidth
              autoComplete="off"
              className="modal-adicionar-amostra-input"
              inputProps={{
                "aria-label": "Código da amostra para escanear",
              }}
            />
            <div className="modal-adicionar-amostra-acoes" style={{ marginTop: "12px" }}>
              <Button
                variant="outlined"
                size="small"
                onClick={consultarTubo}
                disabled={!biomeriuexInput.trim() || processandoConsulta}
              >
                {processandoConsulta ? "Consultando..." : "Consultar"}
              </Button>
            </div>

            {consultaTubo && (
              <div className="modal-adicionar-amostra-info-tubo">
                <h3 className="modal-adicionar-amostra-info-titulo">
                  Confirme os dados da amostra
                </h3>
                <dl className="modal-adicionar-amostra-lista-info">
                  <dt>Paciente:</dt>
                  <dd>{consultaTubo.paciente?.nome || "—"}</dd>
                  <dt>Data de nascimento:</dt>
                  <dd>
                    {consultaTubo.paciente?.dataNascimento
                      ? formatarDataParaBR(
                          new Date(consultaTubo.paciente.dataNascimento)
                        ).split(" ")[0]
                      : "—"}
                  </dd>
                  <dt>Código da amostra:</dt>
                  <dd>{consultaTubo.biomeriuex}</dd>
                  <dt>Código do Tubo:</dt>
                  <dd>{consultaTubo.codigo_tubo || "—"}</dd>
                  <dt>Mnemonicos:</dt>
                  <dd>{consultaTubo.mneumonicos?.trim() || "—"}</dd>
                  <dt>Material:</dt>
                  <dd>{consultaTubo.material || "—"}</dd>
                </dl>
                <div className="modal-adicionar-amostra-acoes">
                  <Button variant="outlined" size="small" onClick={handleLimparConsulta}>
                    Escanear outro
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAdicionarAmostra}
                    disabled={processandoAdicionar}
                  >
                    {processandoAdicionar ? "Adicionando..." : "Adicionar"}
                  </Button>
                </div>
              </div>
            )}
          </Box>
        </Modal>
      </div>
    </>
  );
}
